'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Store, ChevronDown, Check, Layers, Settings } from 'lucide-react';

interface Branch {
  id: string;
  name: string;
  slug: string;
  address?: string | null;
  active: boolean;
  isMain?: boolean;
}

export function BranchSelector() {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<string>('all');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Cargar branchId guardado en localStorage
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('calenddy_selected_branch_id');
      if (saved) setSelectedBranchId(saved);
    }

    const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
    const targetSlug = params?.get('slug') || '';
    const slugQuery = targetSlug ? `?slug=${encodeURIComponent(targetSlug)}` : '';

    fetch(`/api/branches${slugQuery}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.success && data.branches) {
          setBranches(data.branches);
        }
      })
      .catch((err) => console.error('Error fetching branches for selector:', err))
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = (branchId: string) => {
    setSelectedBranchId(branchId);
    setIsOpen(false);
    if (typeof window !== 'undefined') {
      localStorage.setItem('calenddy_selected_branch_id', branchId);
      window.dispatchEvent(
        new CustomEvent('calenddy_branch_changed', {
          detail: { branchId }
        })
      );
    }
  };

  const selectedBranch = branches.find((b) => b.id === selectedBranchId);

  // Si solo hay 1 sucursal o ninguna, mostrar de manera compacta sin selector complejo
  if (!loading && branches.length <= 1) {
    const single = branches[0];
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200/90 rounded-xl shadow-xs text-xs font-semibold text-slate-700">
        <Store className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
        <span className="truncate max-w-[140px] sm:max-w-[200px]">{single?.name || 'Sede Principal'}</span>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-200/90 rounded-xl shadow-xs text-xs font-semibold text-slate-800 transition-colors cursor-pointer select-none"
      >
        {selectedBranchId === 'all' ? (
          <>
            <Layers className="w-3.5 h-3.5 text-blue-600 shrink-0" />
            <span className="truncate max-w-[130px] sm:max-w-[180px]">Todas las sedes</span>
          </>
        ) : (
          <>
            <Store className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
            <span className="truncate max-w-[130px] sm:max-w-[180px]">
              {selectedBranch?.name || 'Sucursal'}
            </span>
          </>
        )}
        <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-slate-200/90 py-1.5 z-50 animate-in fade-in zoom-in-95 duration-100">
            <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 flex items-center justify-between">
              <span>Filtrar por sucursal</span>
              <span className="text-slate-500 font-normal">{branches.length} sedes</span>
            </div>

            <button
              onClick={() => handleSelect('all')}
              className={`w-full px-3 py-2 text-left text-xs flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer ${
                selectedBranchId === 'all' ? 'font-bold text-blue-600 bg-blue-50/50' : 'text-slate-700'
              }`}
            >
              <div className="flex items-center gap-2 truncate">
                <Layers className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                <span className="truncate">Todas las sedes (Consolidado)</span>
              </div>
              {selectedBranchId === 'all' && <Check className="w-3.5 h-3.5 text-blue-600 shrink-0" />}
            </button>

            <div className="my-1 border-t border-slate-100" />

            <div className="max-h-60 overflow-y-auto">
              {branches.map((b) => (
                <button
                  key={b.id}
                  onClick={() => handleSelect(b.id)}
                  className={`w-full px-3 py-2 text-left text-xs flex items-center justify-between hover:bg-slate-50 transition-colors cursor-pointer ${
                    selectedBranchId === b.id ? 'font-bold text-emerald-700 bg-emerald-50/50' : 'text-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-2 truncate">
                    <Store className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                    <div className="truncate">
                      <div className="truncate">{b.name}</div>
                      {b.address && (
                        <div className="text-[10px] text-slate-400 font-normal truncate max-w-[180px]">
                          {b.address}
                        </div>
                      )}
                    </div>
                  </div>
                  {selectedBranchId === b.id && <Check className="w-3.5 h-3.5 text-emerald-700 shrink-0" />}
                </button>
              ))}
            </div>

            <div className="mt-1 pt-1 border-t border-slate-100 px-2">
              <Link
                href="/dashboard/branches"
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
              >
                <Settings className="w-3.5 h-3.5 text-slate-400" />
                <span>Gestionar sucursales</span>
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
